import { FormGroup, FormSection, Radio } from '@ksyos/design-system';
import type { IOsasReferralData } from '@common/validation/customControls/osas/osasReferralSchemas/osasReferralSchema';
import type { ValidationErrors } from '@frontend/utils';
import { YesOrNoOrUnknown } from '@common/enums';
import { RadioGroupFormGroup, TextAreaFormGroup } from '../../../processActivityFormGroupFields';
import { ExaminationDetailsModal } from './ExaminationDetailsModal';
import { ImageSelect, retrognathismOptions, tongueSizeOptions, tonsilSizeOptions } from './ImageSelect';

type PhysicalExaminationFormSectionProps = {
    data: IOsasReferralData;
    errors: ValidationErrors;
    onChange: (data: Partial<IOsasReferralData>, validateKeys?: string | string[] | undefined) => void;
};

export function PhysicalExaminationFormSection(props: Readonly<PhysicalExaminationFormSectionProps>) {
    const { data, errors, onChange } = props;

    return (
        <FormSection title="Lichamelijk onderzoek">
            <ExaminationDetailsModal />

            <FormGroup
                hasErrors={errors.has('tonsilSize')}
                label="Selecteer tonsilgrootte"
            >
                <ImageSelect
                    keyName={'tonsilSize'}
                    onChange={onChange}
                    options={tonsilSizeOptions}
                    value={data.tonsilSize}
                />
            </FormGroup>

            <FormGroup
                hasErrors={errors.has('tongueSize')}
                label="Selecteer tonggrootte"
            >
                <ImageSelect
                    keyName={'tongueSize'}
                    onChange={onChange}
                    options={tongueSizeOptions}
                    value={data.tongueSize}
                />
            </FormGroup>

            <FormGroup
                hasErrors={errors.has('retrognathism')}
                label="Selecteer retrognathie"
            >
                <ImageSelect
                    keyName={'retrognathism'}
                    onChange={onChange}
                    options={retrognathismOptions}
                    value={data.retrognathism}
                />
            </FormGroup>

            <RadioGroupFormGroup
                fieldKey="isDentitionWellSanitized"
                isRequired={true}
                label="Dentitie goed gesaneerd"
                onChange={async (value) => {
                    onChange({ isDentitionWellSanitized: value as YesOrNoOrUnknown }, 'isDentitionWellSanitized');
                }}
                radioGroupLayout="row"
                value={(data.isDentitionWellSanitized as YesOrNoOrUnknown) ?? undefined}
            >
                <Radio
                    key={YesOrNoOrUnknown.yes}
                    label="Ja"
                    value={YesOrNoOrUnknown.yes}
                />
                <Radio
                    key={YesOrNoOrUnknown.no}
                    label="Nee"
                    value={YesOrNoOrUnknown.no}
                />
                <Radio
                    key={YesOrNoOrUnknown.unknown}
                    label="Onbekend"
                    value={YesOrNoOrUnknown.unknown}
                />
            </RadioGroupFormGroup>

            <TextAreaFormGroup
                fieldKey="isDentitionWellSanitizedRemark"
                label="Toelichting dentitie"
            />

            <RadioGroupFormGroup
                fieldKey="hasDentures"
                isRequired={true}
                label="Gebitsprothese"
                onChange={async (value) => {
                    onChange({ hasDentures: value as YesOrNoOrUnknown }, 'hasDentures');
                }}
                radioGroupLayout="row"
                value={(data.hasDentures as YesOrNoOrUnknown) ?? undefined}
            >
                <Radio
                    key={YesOrNoOrUnknown.yes}
                    label="Ja"
                    value={YesOrNoOrUnknown.yes}
                />
                <Radio
                    key={YesOrNoOrUnknown.no}
                    label="Nee"
                    value={YesOrNoOrUnknown.no}
                />
                <Radio
                    key={YesOrNoOrUnknown.unknown}
                    label="Onbekend"
                    value={YesOrNoOrUnknown.unknown}
                />
            </RadioGroupFormGroup>
        </FormSection>
    );
}
